import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./FlowsView.css";

function FlowCard({ flow, onStart, t }) {
  return (
    <div className="cb-flow-card" onClick={() => onStart(flow.id)}>
      <div className="cb-flow-icon">{flow.icon || "🧭"}</div>
      <h3>{flow.title}</h3>
      <p>{flow.description}</p>
      <div className="cb-flow-meta">
        <span>⏱ {flow.duration}</span>
        <span>❓ {t("cycleBot.flowsView.questionsCount", { count: flow.questions_count || 0 })}</span>
      </div>
      <button className="cb-btn-primary cb-flow-start-btn">
        {t("cycleBot.flowsView.start")}
      </button>
    </div>
  );
}

function FlowQuestion({ flow, session, question, progress, onAnswer, onBack, onCancel, t }) {
  const [selected, setSelected] = useState([]);

  const isMulti = question?.type === "multiple";

  const handleSingle = (value) => {
    onAnswer(value);
    setSelected([]);
  };

  const handleMultiToggle = (value) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleMultiSubmit = () => {
    if (selected.length > 0) {
      onAnswer(selected);
      setSelected([]);
    }
  };

  const pct = progress
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <div className="cb-flow-container">
      <div className="cb-flow-header">
        <h2>{flow?.title}</h2>
        <div className="cb-flow-progress">
          <span>{progress?.current}/{progress?.total}</span>
          <div className="cb-progress-bar">
            <div className="cb-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <span>{pct}%</span>
        </div>
      </div>

      <div className="cb-flow-question">
        <h3>{question?.text}</h3>
      </div>

      {isMulti ? (
        <>
          <div className="cb-flow-checkboxes">
            {question?.options?.map((opt) => (
              <label key={opt.value} className="cb-flow-checkbox-label">
                <input
                  type="checkbox"
                  checked={selected.includes(opt.value)}
                  onChange={() => handleMultiToggle(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <button
            className="cb-btn-primary"
            disabled={selected.length === 0}
            onClick={handleMultiSubmit}
          >
            {t("cycleBot.flowsView.next")} →
          </button>
        </>
      ) : (
        <div className="cb-flow-options">
          {question?.options?.map((opt) => (
            <button
              key={opt.value}
              className="cb-flow-option-btn"
              onClick={() => handleSingle(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      <div className="cb-flow-nav">
        <button className="cb-btn-secondary" onClick={onBack}>⬅️ {t("cycleBot.flowsView.back")}</button>
        <button className="cb-btn-secondary" onClick={onCancel}>✖️ {t("cycleBot.flowsView.cancel")}</button>
      </div>
    </div>
  );
}


/* ─── Écran génération IA ─── */
const GENERATION_STEPS = [
  { icon: "🔍", labelKey: "cycleBot.flowsView.genAnalyzeAnswers", duration: 1800 },
  { icon: "🧬", labelKey: "cycleBot.flowsView.genCorrelateCycle", duration: 1800 },
  { icon: "🤖", labelKey: "cycleBot.flowsView.genGenerateReport", duration: 2200 },
  { icon: "✨", labelKey: "cycleBot.flowsView.genFinalize",       duration: 1200 },
];

function GeneratingReport({ flowTitle }) {
  const { t } = useTranslation("cycle");
  const [step, setStep]       = React.useState(0);
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    let idx = 0;
    const next = () => {
      idx++;
      if (idx < GENERATION_STEPS.length) {
        setVisible(false);
        setTimeout(() => { setStep(idx); setVisible(true); }, 250);
        setTimeout(next, GENERATION_STEPS[idx]?.duration || 1800);
      }
    };
    const t = setTimeout(next, GENERATION_STEPS[0].duration);
    return () => clearTimeout(t);
  }, []);

  const pct = Math.round(((step + 1) / GENERATION_STEPS.length) * 100);
  const current = GENERATION_STEPS[step];

  return (
    <div className="cb-gen-wrap">
      {/* Cercle animé */}
      <div className="cb-gen-orb">
        <svg className="cb-gen-ring" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" className="cb-gen-ring-bg" />
          <circle cx="60" cy="60" r="52" className="cb-gen-ring-fill"
            style={{ strokeDashoffset: `${326 - (326 * pct) / 100}` }} />
        </svg>
        <div className="cb-gen-icon-wrap">
          <span className={`cb-gen-icon ${visible ? "in" : "out"}`}>{current.icon}</span>
        </div>
      </div>

      {/* Titre */}
      <div className="cb-gen-title">{t("cycleBot.flowsView.generatingReport")}</div>
      {flowTitle && <div className="cb-gen-subtitle">{flowTitle}</div>}

      {/* Étape courante */}
      <div className={`cb-gen-step-label ${visible ? "in" : "out"}`}>
        {t(current.labelKey)}
      </div>

      {/* Barre de progression */}
      <div className="cb-gen-bar-wrap">
        <div className="cb-gen-bar">
          <div className="cb-gen-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="cb-gen-pct">{pct}%</span>
      </div>

      {/* Dots */}
      <div className="cb-gen-dots">
        {GENERATION_STEPS.map((s, i) => (
          <div key={i} className={`cb-gen-dot ${i < step ? "done" : i === step ? "active" : ""}`}>
            {i < step ? "✓" : i + 1}
          </div>
        ))}
      </div>

      {/* Particles flottantes */}
      <div className="cb-gen-particles" aria-hidden="true">
        {["✦","◆","●","✦","◆","●","✦","◆"].map((s, i) => (
          <span key={i} className="cb-gen-particle" style={{ "--i": i }}>{s}</span>
        ))}
      </div>
    </div>
  );
}

function FlowReport({ report, onRestart, onGoChat, t }) {
  const severity = report?.summary?.severity || report?.summary?.lifestyle_score;

  return (
    <div className="cb-flow-report">

      {/* ── En-tête ── */}
      <div className="cb-flow-report-header">
        <div className="cb-flow-report-icon">📋</div>
        <div className="cb-flow-report-title">{report?.flow_title}</div>
        {severity && (
          <div className="cb-flow-report-summary">
            {typeof severity === "number"
              ? <>{t("cycleBot.flowsView.lifestyleScore")} : <strong>{severity}/100</strong></>
              : <>{t("cycleBot.flowsView.severity")} : <strong>{severity}</strong></>
            }
          </div>
        )}
        {report?.red_flags?.map((flag, i) => (
          <div key={i} className="cb-flow-report-priority critical">
            🚨 {flag}
          </div>
        ))}
      </div>

      {/* ── Analyse IA ── */}
      {report?.analysis && (
        <div className="cb-flow-report-section cb-flow-report-analysis">
          <h3>📊 {t("cycleBot.flowsView.analysis")}</h3>
          <p style={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>{report.analysis}</p>
        </div>
      )}

      {/* ── Recommandations ── */}
      {report?.recommendations?.map((rec, i) => (
        <div key={i} className={`cb-flow-report-section cb-flow-rec-${rec.priority || "medium"}`}>
          <h3>
            {rec.priority === "high" ? "🔴" : rec.priority === "medium" ? "🟡" : "🟢"} {rec.title}
          </h3>
          <ul className="cb-flow-report-recs">
            {rec.items?.map((item, j) => <li key={j}>{item}</li>)}
          </ul>
        </div>
      ))}

      {/* ── Prochaines étapes ── */}
      {report?.next_steps?.length > 0 && (
        <div className="cb-flow-report-section">
          <h3>🗓️ {t("cycleBot.flowsView.nextSteps")}</h3>
          <ul className="cb-flow-report-recs">
            {report.next_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}

      {/* ── Message IA personnalisé ── */}
      {report?.ai_message && (
        <div className="cb-flow-ai-message">
          <span className="cb-flow-ai-icon">🤖</span>
          <p>{report.ai_message}</p>
        </div>
      )}

      {/* ── Disclaimer médical ── */}
      {report?.disclaimer && (
        <div className="cb-flow-disclaimer">
          <span>⚕️</span>
          <p>{report.disclaimer}</p>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="cb-flow-report-actions">
        <button className="cb-btn-primary" onClick={onGoChat}>
          💬 {t("cycleBot.flowsView.chatWithAssistant")}
        </button>
        <button className="cb-btn-secondary" onClick={onRestart}>
          🔄 {t("cycleBot.flowsView.newFlow")}
        </button>
      </div>
    </div>
  );
}

export default function FlowsView({ flows, onStartFlow, activeSession, onAnswer, onBack, onCancel, report, onRestartFlow, onGoChat, sessionLoading, reportLoading, isGeneratingReport }) {
  const { t } = useTranslation("cycle");
  // Affiche l'écran de génération quand c'est la dernière question (progress.current === progress.total)
  const [generating, setGenerating] = React.useState(false);
  const [genFlowTitle, setGenFlowTitle] = React.useState("");

  // Reset si un report arrive ou si une nouvelle session commence
  React.useEffect(() => {
    if (report)         setGenerating(false);
    if (!activeSession && !report && !sessionLoading) setGenerating(false);
  }, [report, activeSession, sessionLoading]);

  // Wrapper onAnswer pour détecter la dernière question
  const handleAnswer = React.useCallback((value) => {
    const prog = activeSession?.progress;
    if (prog && prog.current === prog.total) {
      setGenerating(true); // Dernière question → afficher l'écran de génération
      setGenFlowTitle(activeSession?.flow?.title || "");
    }
    onAnswer(value);
  }, [onAnswer, activeSession]);

  const isGenerating = generating && !report;
  const showList     = !activeSession && !report && !isGenerating;
  const showActive   = !!activeSession && !report && !isGenerating;
  const showReport   = !!report && !isGenerating;

  return (
    <div className="cb-view cb-view--active cb-flows-theme">
      <div className="cb-view-head">
        <div>
          <h1>🧭 {t("cycleBot.flowsView.title")}</h1>
          <p className="cb-view-sub">{t("cycleBot.flowsView.subtitle")}</p>
        </div>
      </div>

      {isGenerating && (
        <GeneratingReport flowTitle={genFlowTitle} />
      )}

      {showList && (
        <div className="cb-flows-grid">
          {flows?.length > 0 ? (
            flows.map((f) => (
              <FlowCard key={f.id} flow={f} onStart={onStartFlow} t={t} />
            ))
          ) : (
            <div className="cb-empty">{t("cycleBot.flowsView.loadingFlows")}</div>
          )}
        </div>
      )}

      {showActive && (
        <FlowQuestion
          flow={activeSession.flow}
          session={activeSession.session_id}
          question={activeSession.current_question}
          progress={activeSession.progress}
          onAnswer={handleAnswer}
          onBack={onBack}
          onCancel={onCancel}
          t={t}
        />
      )}

      {showReport && (
        <FlowReport
          report={report}
          onRestart={onRestartFlow}
          onGoChat={onGoChat}
          t={t}
        />
      )}
    </div>
  );
}
